<template>
  <div class="frames-view-container deck-view-container">
    <div
      v-if="activeFrames.length === 0"
      class="frames-empty-state">
      <v-icon
        size="48"
        color="grey">
        mdi-monitor-off
      </v-icon>
      <div class="mt-2">No frames configured</div>
    </div>
    <template v-else>
      <div class="deck-frame-selector">
        <v-select
          v-model="selectedFrameId"
          :items="frameSelectItems"
          item-title="title"
          item-value="value"
          density="compact"
          variant="outlined"
          hide-details
          single-line />
      </div>
      <div class="deck-queues-container">
        <template v-if="channelQueues && channelQueues.length > 0">
          <div
            v-for="queue in channelQueues"
            :key="queue.did"
            class="frame-column deck-queue-column">
            <template v-if="queue.did && deluge.items[queue.did]">
              <div
                class="queue-header"
                :class="{ 'queue-live': getVisibleQueue(queue).live }">
                <v-icon
                  v-if="isNested(queue)"
                  size="small"
                  class="mr-1 deck-back-btn"
                  @click.stop="navigateBack(queue)"
                  title="Go back">
                  mdi-arrow-left
                </v-icon>
                <v-icon
                  v-else
                  size="small"
                  class="mr-2">
                  mdi-playlist-play
                </v-icon>
                <span class="queue-header-name">
                  {{ getQueueBreadcrumb(queue) }}
                </span>
              </div>
              <div
                v-if="getVisibleQueue(queue).items"
                class="queue-items">
                <template
                  v-for="(item, idx) in getVisibleQueue(queue).items"
                  :key="item?.did || idx">
                  <!-- Queue-type items: navigate into -->
                  <div
                    v-if="item?.type === 'queue' && item.did && deluge.items[item.did]"
                    class="scene-row"
                    :class="{
                      'scene-live': item.live,
                      'scene-current': idx === getVisibleQueue(queue).index && !item.live,
                    }"
                    @click="navigateInto(queue, idx)"
                    :title="`Open: ${deluge.items[item.did].name}`">
                    <v-icon
                      size="small"
                      class="scene-icon mr-2">
                      mdi-folder-outline
                    </v-icon>
                    <span class="scene-name">
                      {{ deluge.items[item.did].name }}
                    </span>
                    <v-icon
                      size="small"
                      class="ml-auto scene-go-icon"
                      style="opacity: 0.5">
                      mdi-chevron-right
                    </v-icon>
                  </div>
                  <!-- Scene items: force live on click -->
                  <div
                    v-else-if="item?.did && deluge.items[item.did]"
                    class="scene-row"
                    :class="{
                      'scene-live': item.live,
                      'scene-current': idx === getVisibleQueue(queue).index && !item.live,
                    }"
                    @click="forceLive(item.did)"
                    :title="
                      item.live
                        ? 'Currently live'
                        : `Force live: ${deluge.items[item.did].name}`
                    ">
                    <v-icon
                      size="small"
                      class="scene-icon mr-2">
                      {{ getTemplateIcon(deluge.items[item.did]) }}
                    </v-icon>
                    <span class="scene-name">
                      {{ deluge.items[item.did].name }}
                    </span>
                    <v-progress-circular
                      v-if="loadingDid === item.did"
                      indeterminate
                      color="white"
                      :size="18"
                      :width="2"
                      class="ml-auto" />
                    <v-icon
                      v-else-if="item.live"
                      size="small"
                      class="ml-auto live-icon">
                      mdi-broadcast
                    </v-icon>
                    <v-icon
                      v-else
                      size="small"
                      class="ml-auto scene-go-icon">
                      mdi-chevron-double-right
                    </v-icon>
                  </div>
                </template>
              </div>
              <div class="deck-queue-footer">
                <div
                  class="scene-row deck-footer-row"
                  :class="{ 'scene-live': getVisibleQueue(queue).live }"
                  @click="forceLive(getVisibleQueue(queue).did)"
                  :title="`Force live: ${deluge.items[getVisibleQueue(queue).did]?.name || 'Queue'}`">
                  <v-progress-circular
                    v-if="loadingDid === getVisibleQueue(queue).did"
                    indeterminate
                    color="white"
                    :size="18"
                    :width="2" />
                  <v-icon
                    v-else
                    size="small">
                    mdi-chevron-double-right
                  </v-icon>
                </div>
              </div>
            </template>
          </div>
        </template>
        <div
          v-else
          class="frame-no-queues">
          No queues for this frame
        </div>
      </div>
    </template>
  </div>
</template>
<script setup>
import { computed, ref, watch, reactive } from 'vue';
import { deluge } from '@/stores/deluge.js';
import { $http } from '@/stores/http.js';

const storedFrame = localStorage.getItem('cp_deckFrameId');
const selectedFrameId = ref(storedFrame ? Number(storedFrame) : null);
const nestedPaths = reactive({});
const loadingDid = ref(null);

watch(selectedFrameId, (id) => {
  if (id !== null) localStorage.setItem('cp_deckFrameId', String(id));
});

const activeFrames = computed(() => {
  if (!deluge.frames) return [];
  return Object.values(deluge.frames)
    .filter((frame) => frame.did && deluge.channels?.[frame.did])
    .sort((a, b) => a.id - b.id);
});

const frameSelectItems = computed(() => {
  return activeFrames.value.map((frame) => ({
    title: frame.nickname || `Frame ${frame.number}`,
    value: frame.id,
  }));
});

// Auto-select first frame
watch(
  activeFrames,
  (frames) => {
    if (frames.length > 0 && (selectedFrameId.value === null || !frames.find((f) => f.id === selectedFrameId.value))) {
      selectedFrameId.value = frames[0].id;
    }
  },
  { immediate: true }
);

// Reset nested paths when frame changes
watch(selectedFrameId, () => {
  Object.keys(nestedPaths).forEach((key) => delete nestedPaths[key]);
});

const selectedFrame = computed(() => {
  if (!deluge.frames || selectedFrameId.value === null) return null;
  return Object.values(deluge.frames).find((f) => f.id === selectedFrameId.value) || null;
});

const channelDid = computed(() => {
  return selectedFrame.value?.did || null;
});

const channelQueues = computed(() => {
  if (!channelDid.value) return null;
  const channel = deluge.channels?.[channelDid.value];
  return channel?.queues || null;
});

function getQueueId(queue) {
  if (!queue?.did || !deluge.items[queue.did]) return null;
  return deluge.items[queue.did].id;
}

function getVisibleQueue(queue) {
  if (!deluge.items[queue.did]) return queue;
  const id = getQueueId(queue);
  let qtemp = queue;
  if (nestedPaths[id] && nestedPaths[id].length > 0) {
    for (let i = 0; i < nestedPaths[id].length; i++) {
      const nextIndex = nestedPaths[id][i];
      const nextItem = qtemp.items?.[nextIndex];
      if (!nextItem || !deluge.items[nextItem.did]) {
        // Path points to deleted item, clear remaining path
        nestedPaths[id] = nestedPaths[id].slice(0, i);
        return qtemp;
      }
      qtemp = nextItem;
    }
  }
  return qtemp;
}

function getQueueBreadcrumb(queue) {
  if (!deluge.items[queue.did]) return '';
  const id = getQueueId(queue);
  let name = deluge.items[queue.did].name;
  let qtemp = queue;
  if (nestedPaths[id] && nestedPaths[id].length > 0) {
    for (let i = 0; i < nestedPaths[id].length; i++) {
      const nextIndex = nestedPaths[id][i];
      const nextItem = qtemp.items?.[nextIndex];
      if (!nextItem || !deluge.items[nextItem.did]) break;
      name = name + ' / ' + deluge.items[nextItem.did].name;
      qtemp = nextItem;
    }
  }
  return name;
}

function isNested(queue) {
  const id = getQueueId(queue);
  return id && nestedPaths[id] && nestedPaths[id].length > 0;
}

function navigateInto(queue, itemIndex) {
  const id = getQueueId(queue);
  if (!id) return;
  if (!nestedPaths[id]) {
    nestedPaths[id] = [];
  }
  nestedPaths[id].push(itemIndex);
}

function navigateBack(queue) {
  const id = getQueueId(queue);
  if (!id || !nestedPaths[id]) return;
  nestedPaths[id].pop();
}

function getTemplateIcon(item) {
  if (!item?.scene_name) return 'mdi-cube';
  const template = deluge.templates?.[item.scene_name];
  return template?.icon || 'mdi-cube';
}

async function forceLive(did) {
  if (!did || !channelDid.value || loadingDid.value) return;
  loadingDid.value = did;
  try {
    await $http(
      'GET',
      '/dynamic_engine/go_live?did=' + did + '&channel_did=' + channelDid.value
    );
  } catch (error) {
    console.error('Failed to force live:', error);
  }
  loadingDid.value = null;
}
</script>
<style scoped>
.deck-view-container {
  flex-direction: column;
  gap: 8px;
}

.deck-frame-selector {
  flex-shrink: 0;
  max-width: 300px;
}

.deck-queues-container {
  display: flex;
  gap: 12px;
  overflow-x: auto;
  overflow-y: hidden;
  flex-grow: 1;
  align-items: stretch;
}

.deck-queue-column {
  display: flex;
  flex-direction: column;
}

.deck-queue-column .queue-items {
  flex-grow: 1;
  overflow-y: auto;
}

.deck-queue-footer {
  flex-shrink: 0;
  padding: 8px;
  padding-top: 4px;
}

.deck-queue-footer .deck-footer-row {
  justify-content: center;
  background: rgba(255, 255, 255, 0.04);
}

.deck-back-btn {
  cursor: pointer;
  border-radius: 4px;
  padding: 2px;
}

.deck-back-btn:hover {
  background: rgba(255, 255, 255, 0.15);
}

/* Reuse FramesView class names for theme override compatibility */
.frames-view-container {
  display: flex;
  gap: 12px;
  overflow-x: auto;
  overflow-y: hidden;
  padding: 8px 4px;
  flex-grow: 1;
  align-items: stretch;
}

.frames-empty-state {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  width: 100%;
  color: #888;
  font-size: 16px;
}

.frame-column {
  min-width: 280px;
  max-width: 280px;
  background: var(--v-backgroundColor-base);
  border-radius: 8px;
  overflow: hidden;
  flex-shrink: 0;
}

.queue-header {
  display: flex;
  align-items: center;
  padding: 10px 12px;
  font-size: 14px;
  font-weight: 600;
  color: #ccc;
  background: var(--v-cardColor-base);
  border-radius: 6px;
  user-select: none;
}

.queue-header.queue-live {
  color: white;
  background: rgb(var(--v-theme-primary));
}

.queue-header-name {
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}

.queue-items {
  display: flex;
  flex-direction: column;
  gap: 2px;
  padding: 8px;
}

.scene-row {
  display: flex;
  align-items: center;
  min-height: 44px;
  padding: 10px 12px;
  font-size: 14px;
  color: #bbb;
  background: var(--v-cardColor-base);
  border-radius: 6px;
  cursor: pointer;
  user-select: none;
  transition:
    background 0.15s ease,
    transform 0.1s ease;
}

.scene-row:hover {
  background: rgba(255, 255, 255, 0.08);
  color: white;
}

.scene-row:active {
  transform: scale(0.97);
}

.scene-row.scene-live {
  background: rgb(var(--v-theme-primary));
  color: white;
}

.scene-row.scene-current {
  border-left: 3px solid rgb(var(--v-theme-primary));
}

.scene-name {
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
  flex: 1;
  min-width: 0;
}

.live-icon {
  color: white;
}

.scene-go-icon {
  opacity: 0;
  transition: opacity 0.15s ease;
}

.scene-row:hover .scene-go-icon {
  opacity: 0.7;
}

.frame-no-queues {
  display: flex;
  align-items: center;
  justify-content: center;
  color: #666;
  font-size: 14px;
  padding: 24px;
}
</style>
